import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getHolidays,
  deleteHoliday,
  getWeeklyOffs,
  updateWeeklyOffs,
} from "services/hrms.api";
import { Plus, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import HolidayTable from "./HolidayTable";
import WeeklyOffPicker from "./WeeklyOffPicker";
import Loading from "components/common/Loading";

/* ─── date helpers ───────────────────────────────────────────── */

const safeToDate = (v) => {
  if (!v) return new Date("Invalid");
  if (v instanceof Date) return v;
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(v + "T00:00:00");
    return new Date(v);
  }
  return new Date(v);
};

const toYMD = (d) => {
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const DOW_LABEL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

function normalizeWeeklyOffs(arr) {
  if (!Array.isArray(arr)) return [];
  return [...new Set(arr.map((x) => Number(x)))]
    .filter((x) => Number.isInteger(x) && x >= 0 && x <= 6)
    .sort((a, b) => a - b);
}

/* ─── component ──────────────────────────────────────────────── */

export default function HolidayList() {
  const nav = useNavigate();

  const [rows, setRows]                   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [savingWeekly, setSavingWeekly]   = useState(false);

  // Company-level weekly off — the original feature.
  // Employees who have NO per-employee override will use this value.
  const [weeklyOffDays, setWeeklyOffDays] = useState([0, 6]);

  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  /* ── load ── */
  const load = async () => {
    try {
      setLoading(true);
      const [holRes, weeklyRes] = await Promise.all([
        getHolidays(),
        getWeeklyOffs().catch(() => ({ data: { weeklyOffDays: [0, 6] } })),
      ]);
      setRows(holRes?.data?.items || holRes?.data || []);
      const days = normalizeWeeklyOffs(weeklyRes?.data?.weeklyOffDays);
      setWeeklyOffDays(days.length ? days : [0, 6]);
    } catch (err) {
      console.error("Error loading holidays:", err);
      setRows([]);
      setWeeklyOffDays([0, 6]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /* ── save company-level weekly off ──
     This updates /hrms-settings/weekly-offs (company default).
     Per-employee overrides are managed separately in EmployeeWeeklyOffPicker.
  ── */
  const handleSaveWeeklyOffs = async (nextDays) => {
    try {
      setSavingWeekly(true);
      const cleaned = normalizeWeeklyOffs(nextDays);
      const res     = await updateWeeklyOffs(cleaned);          // PUT /hrms-settings/weekly-offs
      const saved   = normalizeWeeklyOffs(res?.data?.weeklyOffDays);
      setWeeklyOffDays(saved.length ? saved : cleaned);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to save weekly offs.");
    } finally {
      setSavingWeekly(false);
    }
  };

  /* ── calendar helpers ── */
  const y          = viewDate.getFullYear();
  const m          = viewDate.getMonth();
  const monthEnd   = new Date(y, m + 1, 0);
  const firstDow   = new Date(y, m, 1).getDay();
  const totalDays  = monthEnd.getDate();

  const startOfGrid = new Date(y, m, 1 - firstDow);
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(startOfGrid);
    d.setDate(startOfGrid.getDate() + i);
    return d;
  });

  const monthHolidays = useMemo(
    () => rows.filter((r) => {
      const d = safeToDate(r.date);
      return !isNaN(d) && d.getFullYear() === y && d.getMonth() === m;
    }),
    [rows, y, m]
  );

  const holidayByDate = useMemo(() => {
    const map = new Map();
    monthHolidays.forEach((h) => {
      const d = safeToDate(h.date);
      if (!isNaN(d)) map.set(toYMD(d), h);
    });
    return map;
  }, [monthHolidays]);

  const weeklyOffCount = useMemo(() => {
    let c = 0;
    for (let i = 1; i <= totalDays; i++) {
      if (weeklyOffDays.includes(new Date(y, m, i).getDay())) c++;
    }
    return c;
  }, [y, m, totalDays, weeklyOffDays]);

  const holidaysOnWorkingDays = useMemo(() => {
    let c = 0;
    monthHolidays.forEach((h) => {
      const d = safeToDate(h.date);
      if (!isNaN(d) && !weeklyOffDays.includes(d.getDay())) c++;
    });
    return c;
  }, [monthHolidays, weeklyOffDays]);

  const workingDays = Math.max(0, totalDays - weeklyOffCount - holidaysOnWorkingDays);
  const monthLabel  = viewDate.toLocaleString("en-US", { month: "long", year: "numeric" });

  const allHolidaysSorted = useMemo(
    () => [...monthHolidays].sort((a, b) => String(a.date).localeCompare(String(b.date))),
    [monthHolidays]
  );

  /* ── render ── */
  if (loading && rows.length === 0) {
    return <div className="p-4 md:p-6"><Loading variant="dots" fullscreen /></div>;
  }

  return (
    <div className="relative space-y-6 p-4 md:p-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold dark:text-white">Manage Holidays</h2>

        {/* Navigate to per-employee weekly off page */}
        <button
          onClick={() => nav("/admin/hrms/holidays/employee-weekly-offs")}
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm
                     hover:bg-gray-100 dark:border-gray-700 dark:text-white dark:hover:bg-white/10"
        >
          <CalendarDays className="h-4 w-4" />
          Employee Weekly Off
        </button>

        <button
          onClick={() => nav("/admin/hrms/holidays/new")}
          className="hidden items-center gap-2 rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-4 py-2 text-sm text-white hover:opacity-95 md:flex"
        >
          <Plus className="h-4 w-4" /> Add Holiday
        </button>
      </div>

      {/* ── Company-level Weekly Off Picker (original feature) ──
          This sets the default for ALL employees.
          Employees with a per-employee override (set in EmployeeWeeklyOffPicker)
          will use their own days instead — this does NOT affect them.
      ── */}
      <WeeklyOffPicker
        value={weeklyOffDays}
        saving={savingWeekly}
        onSave={handleSaveWeeklyOffs}
      />

      {/* Calendar — highlights company-level weekly offs */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => { const d = new Date(viewDate); d.setMonth(d.getMonth() - 1); setViewDate(d); }}
              className="rounded-lg border border-gray-200 p-1.5 dark:border-white/10 dark:text-white"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-sm font-semibold dark:text-white">{monthLabel}</div>
            <button
              onClick={() => { const d = new Date(viewDate); d.setMonth(d.getMonth() + 1); setViewDate(d); }}
              className="rounded-lg border border-gray-200 p-1.5 dark:border-white/10 dark:text-white"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={() => { const d = new Date(); d.setDate(1); setViewDate(d); }}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs dark:border-white/10 dark:text-white"
          >
            Today
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 rounded-md bg-gray-50 p-2 text-center text-xs font-medium text-gray-500 dark:bg-white/5">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>

        {/* Cells */}
        <div className="mt-2 grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const inMonth     = d.getMonth() === m;
            const key         = toYMD(d);
            const isToday     = toYMD(new Date()) === key && inMonth;
            const dow         = d.getDay();
            const holiday     = holidayByDate.get(key);
            const isWeeklyOff = weeklyOffDays.includes(dow);

            return (
              <div
                key={i}
                className={[
                  "h-16 rounded-md border text-center text-sm flex flex-col items-center justify-center",
                  "border-gray-200 dark:border-white/10",
                  inMonth ? "" : "opacity-40",
                  isToday ? "ring-2 ring-brand-500 ring-offset-1" : "",
                  isWeeklyOff && !holiday ? "bg-yellow-50 dark:bg-white/5" : "",
                  holiday ? "bg-rose-50 dark:bg-white/10 border-rose-200" : "",
                ].join(" ")}
                title={
                  holiday
                    ? `${holiday.name}${holiday.type ? ` • ${holiday.type}` : ""}`
                    : isWeeklyOff
                    ? `Weekly Off • ${DOW_LABEL[dow]}`
                    : ""
                }
              >
                <div className="text-xs text-gray-500">{d.getDate()}</div>
                {holiday ? (
                  <div className="mt-1 truncate px-2 text-[11px] font-medium text-rose-600 dark:text-white">
                    {holiday.name}
                  </div>
                ) : isWeeklyOff ? (
                  <div className="mt-1 text-[11px] text-amber-600">{DOW_LABEL[dow]}</div>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Summary badges */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] md:grid-cols-4">
          <Badge label="Total Days"   value={totalDays} />
          <Badge label="Holidays"     value={monthHolidays.length} color="bg-amber-100 text-rose-700" />
          <Badge label="Weekly Offs"  value={weeklyOffCount} />
          <Badge label="Working Days" value={workingDays} color="bg-amber-100 text-amber-700" />
        </div>
      </div>

      {/* Holiday list table */}
      <Section title={`Holidays for ${monthLabel}`}>
        <HolidayTable
          items={allHolidaysSorted}
          onDelete={async (id) => { await deleteHoliday(id); load(); }}
        />
      </Section>

      {/* Floating add (mobile) */}
      <button
        onClick={() => nav("/admin/hrms/holidays/new")}
        className="fixed bottom-6 right-6 flex h-11 w-11 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg md:hidden"
        aria-label="Add Holiday"
      >
        <Plus className="h-5 w-5" />
      </button>
    </div>
  );
}

function Badge({ label, value, color = "bg-gray-100 text-gray-700" }) {
  return (
    <div className={`flex items-center justify-between rounded-md px-3 py-2 ${color}`}>
      <span className="text-[11px]">{label}</span>
      <span className="text-xs font-semibold">{value}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800 dark:text-white">
      <div className="mb-2 text-sm font-semibold">{title}</div>
      {children}
    </div>
  );
}
